import { motion } from "framer-motion";
import VerifiedIcon from "@mui/icons-material/Verified";
import { Link } from "react-router-dom";
import React, { useEffect, useState } from "react";
import SkeletonCard from "./SkeletonCard";
import Masonry from "react-masonry-css";

const apiUrl = process.env.REACT_APP_API_URL;

export default function MasonryProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const limit = 40;

  const fetchProducts = (pageNum) => {
    const isInitial = pageNum === 1;
    if (isInitial) setLoading(true);
    else setLoadingMore(true);

    fetch(`${apiUrl}/api/posts?page=${pageNum}&limit=${limit}`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP error ${res.status}`);
        return res.json();
      })
      .then((data) => {
        if (!Array.isArray(data)) {
          setLoading(false);
          setLoadingMore(false);
          return;
        }
        if (data.length < limit) {
          setHasMore(false);
        }
        setProducts((prev) => (isInitial ? data : [...prev, ...data]));
        setLoading(false);
        setLoadingMore(false);
      })
      .catch((err) => {
        console.error(err);
        setLoading(false);
        setLoadingMore(false);
      });
  };

  useEffect(() => {
    fetchProducts(1);
  }, []);

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchProducts(nextPage);
  };

  if (loading) {
    return (
      <Masonry
        breakpointCols={{ default: 4, 1024: 3, 768: 2 }}
        className="my-masonry-grid"
        columnClassName="my-masonry-grid_column"
      >
        {[...new Array(8)].map((_, index) => (
          <div key={index} className="mb-6">
            <SkeletonCard index={index} />
          </div>
        ))}
      </Masonry>
    );
  }

  if (products.length === 0) {
    return <p className="text-center text-gray-500">No items found</p>;
  }

  return (
    <><Masonry
      breakpointCols={{ default: 4, 1024: 3, 768: 2 }}
      className="my-masonry-grid"
      columnClassName="my-masonry-grid_column"
    >
      {products.map((item, index) => {
        return (
          <Link to={`/detail/${item.id}`} key={item.id} className="block mb-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.05 }}
              viewport={{ once: true }}
              className="break-inside-avoid rounded-2xl bg-white border border-gray-100 shadow-sm overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-2 group"
            >
              {/* IMAGE CONTAINER */}
              <div className="relative overflow-hidden aspect-[4/5]">
                <img
                  src={item.image || "/placeholder.jpg"}
                  alt={item.title || "Product image"}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  loading="lazy" />
                {item.verified === 1 && (
                  <div className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-lg flex items-center gap-1 shadow-sm border border-blue-50">
                    <VerifiedIcon className="text-green-500 !w-4 !h-4" />
                    <span className="text-[10px] font-bold text-green-700 uppercase tracking-tight">Verified</span>
                  </div>
                )}
                {(item.condition || item.meta?.Condition || item.meta?.condition) && (
                  <div className="absolute bottom-3 right-3">
                    <span className={`text-[10px] font-bold px-2 py-1 rounded-md uppercase tracking-wider shadow-sm ${(item.condition || item.meta?.Condition || item.meta?.condition) === "Brand New"
                      ? "bg-blue-600 text-white"
                      : "bg-amber-500 text-white"}`}>
                      {item.condition || item.meta?.Condition || item.meta?.condition}
                    </span>
                  </div>
                )}
              </div>

              {/* INFO SECTION */}
              <div className="p-4 space-y-3">
                <div className="space-y-1">
                  <h3 className="font-bold text-gray-900 leading-tight line-clamp-1 group-hover:text-green-600 transition-colors">
                    {(item.meta?.Model && item.meta.Model !== "null" ? item.meta.Model : null)
                      || (item.meta_ids?.Model && item.meta_ids.Model !== "null" ? item.meta_ids.Model : null)
                      || (item.meta?.ItemName && item.meta.ItemName !== "null" ? item.meta.ItemName : null)
                      || item.title
                      || item.meta?.Brand}
                  </h3>
                  <p className="text-xs text-gray-500 font-medium">
                    {[item.meta?.Storage, item.meta?.Gender, item.meta?.Size].find(v => v && v !== "null")} {item.meta?.Color && item.meta.Color !== "null" ? item.meta.Color : ""}
                  </p>
                </div>

                <div className="flex items-center justify-between pt-1">
                  <p className="text-lg font-black text-green-600">
                    {Number(item.price)?.toLocaleString("en-US")}
                    <span className="text-[10px] ml-1 uppercase text-gray-400">ETB</span>
                  </p>
                </div>

                <p className="line-clamp-2 text-xs text-gray-500 leading-relaxed">
                  {item.description}
                </p>

                <div className="pt-2 border-t border-gray-50 flex items-center justify-between text-[10px] text-gray-400 font-medium">

                </div>
              </div>
            </motion.div>
          </Link>
        );
      })}
    </Masonry><div>
        {hasMore && (
          <div className="mt-12 flex justify-center">
            <button
              onClick={handleLoadMore}
              disabled={loadingMore}
              className="px-8 py-3 bg-white border-2 border-green-600 text-green-600 font-bold rounded-xl hover:bg-green-600 hover:text-white transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              {loadingMore ? "Loading..." : "Load More Items"}
            </button>
          </div>
        )}
      </div></>
  );
}
